import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { api, errorMessage } from '../lib/api';

const GameContext = createContext(null);
export const useGame = () => useContext(GameContext);
let bootPromise;
async function bootstrap() {
  let user;
  if (localStorage.getItem('dispatch-token')) {
    try { user = (await api.get('/auth/me')).data; }
    catch (error) { if (error.response?.status !== 401) throw error; localStorage.removeItem('dispatch-token'); }
  }
  if (!user) {
    const {data} = await api.post('/auth/guest');
    localStorage.setItem('dispatch-token',data.token); user=data.user;
  }
  const [game,catalog] = await Promise.all([api.get('/game/current'),api.get('/catalog')]);
  let shift=game.data.shift;
  if (!shift && user.guest) shift=(await api.post('/game/start',{city:'river',duration:0,practice:true,difficulty:'relaxed'})).data.shift;
  return {user, shift, catalog:catalog.data};
}

export const GameProvider = ({children}) => {
  const [user,setUser]=useState(null), [shift,setShift]=useState(null), [catalog,setCatalog]=useState({cities:[],questions:[]});
  const [loading,setLoading]=useState(true), [failure,setFailure]=useState(''), [busy,setBusy]=useState(false), [connected,setConnected]=useState(true);
  const [selectedId,setSelectedId]=useState(null);
  const lastRadio=useRef(null), busyRef=useRef(false), audio=useRef(null), music=useRef(null), userRef=useRef(user);
  userRef.current=user;
  const receive=useCallback(next=>{
    if (!next) return setShift(null);
    setShift(previous => previous?.id===next.id && previous.version>next.version ? previous : next);
  },[]);
  useEffect(()=>{
    if (!bootPromise) bootPromise=bootstrap();
    bootPromise.then(data=>{setUser(data.user);setShift(data.shift);setCatalog(data.catalog);setSelectedId(data.shift?.incidents[0]?.id);setLoading(false);}).catch(error=>{setFailure(errorMessage(error));setLoading(false);bootPromise=null;});
  },[]);
  useEffect(()=>{
    if (!user || shift?.status!=='active') return;
    const id=setInterval(async()=>{
      if(busyRef.current) return;
      try {const {data}=await api.get('/game/current');receive(data.shift);setConnected(true);} catch {setConnected(false);}
    },2000);
    return ()=>clearInterval(id);
  },[user,shift?.status,receive]);
  useEffect(()=>{
    const settings=user?.settings;
    if(!settings) return;
    document.documentElement.dataset.theme=settings.theme;
    document.documentElement.classList.toggle('reduce-motion',!settings.animations);
    document.documentElement.classList.toggle('large-text',settings.large_text);
    document.documentElement.classList.toggle('high-contrast',settings.high_contrast);
  },[user?.settings]);
  const unlockAudio=()=>{
    const AudioContext=window.AudioContext || window.webkitAudioContext;
    if(!AudioContext) return;
    if(!audio.current) audio.current=new AudioContext();
    if(audio.current.state==='suspended') audio.current.resume().catch(()=>{});
  };
  useEffect(()=>{
    document.addEventListener('pointerdown',unlockAudio);
    return ()=>document.removeEventListener('pointerdown',unlockAudio);
  },[]);
  useEffect(()=>{
    if(music.current){music.current.forEach(o=>{try{o.stop();}catch{}});music.current=null;}
    if(!user?.settings.music || !audio.current) return;
    const ctx=audio.current, gain=ctx.createGain(); gain.gain.value=.013*(user.settings.volume/100);gain.connect(ctx.destination);
    music.current=[110,164.81,220].map(freq=>{const o=ctx.createOscillator();o.type='sine';o.frequency.value=freq;o.connect(gain);o.start();return o;});
    return ()=>{music.current?.forEach(o=>{try{o.stop();}catch{}});music.current=null;gain.disconnect();};
  },[user?.settings.music,user?.settings.volume]);
  useEffect(()=>{
    const message=shift?.radio?.at(-1);
    if(!message || message.id===lastRadio.current) return;
    const initial=!lastRadio.current;lastRadio.current=message.id;
    if(initial) return;
    const settings=userRef.current?.settings;
    if(settings?.notifications && message.speaker==='DISPATCH' && message.text.startsWith('New P')) toast.info(message.text);
    if(settings?.sound && audio.current?.state==='running') {
      const ctx=audio.current, osc=ctx.createOscillator(), gain=ctx.createGain();
      osc.frequency.value=880;gain.gain.setValueAtTime(.04*settings.volume/100,ctx.currentTime);gain.gain.exponentialRampToValueAtTime(.001,ctx.currentTime+.13);osc.connect(gain);gain.connect(ctx.destination);osc.start();osc.stop(ctx.currentTime+.14);
    }
    if(settings?.narration && 'speechSynthesis' in window){
      window.speechSynthesis.cancel(); const speech=new SpeechSynthesisUtterance(`${message.speaker}. ${message.text}`);speech.volume=settings.volume/100;speech.rate=1.08;
      speech.pitch={Police:.8,Fire:.7,EMS:1.15,Supervisor:.9}[message.department]||1;
      window.speechSynthesis.speak(speech);
    }
  },[shift?.radio]);
  const mutate=async(url,body)=>{
    if(busyRef.current) return null;
    busyRef.current=true;setBusy(true);
    try {const {data}=await api.post(url,body);receive(data.shift);if(data.organization)window.dispatchEvent(new CustomEvent('dispatch-organization-state',{detail:data.organization}));setConnected(true);return data.shift;}
    catch(error){toast.error(errorMessage(error));return null;}
    finally {setBusy(false);busyRef.current=false;}
  };
  const command=(command,call_id,value,unit_ids)=>mutate('/game/action',{command,call_id,value:value==null?null:String(value),unit_ids:unit_ids||[]});
  const refreshUser=async()=>{const {data}=await api.get('/auth/me');setUser(data);};
  const authenticate=async(mode,form)=>{
    const {data}=await api.post(`/auth/${mode}`,form);localStorage.setItem('dispatch-token',data.token);setUser(data.user);
    const game=await api.get('/game/current');receive(game.data.shift);setSelectedId(game.data.shift?.incidents[0]?.id);toast.success(mode==='register'?'Welcome to the watch. Your progress is saved.':'Welcome back, dispatcher.');
  };
  const logout=async()=>{await api.post('/auth/logout');localStorage.removeItem('dispatch-token');window.location.assign('/');};
  const updateSettings=async(settings)=>{
    try {const {data}=await api.put('/settings',settings);setUser(prev=>({...prev,settings:data.settings}));return true;}
    catch(error){toast.error(errorMessage(error));return false;}
  };
  const value={user,shift,catalog,loading,failure,busy,connected,selectedId,setSelectedId,command,mutate,refreshUser,authenticate,logout,updateSettings,syncShift:receive};
  return <GameContext.Provider value={value}>{children}</GameContext.Provider>;
};